export type ScoreBand =
  | "flat" | "poor" | "fair" | "good" | "great" | "firing" | "hero";

/** Grey used for no-score / off-season / stale dots. Never used for a real band. */
export const NA_COLOUR = "#C9CCD1";

export const SCORE_BAND_DEFINITIONS = [
  { band: "flat", min: 0, colour: "#B23A2E", label: "Flat", displayRange: "0-19" },
  { band: "poor", min: 2, colour: "#D4632F", label: "Poor", displayRange: "20-34" },
  { band: "fair", min: 3.5, colour: "#E2922E", label: "Fair", displayRange: "35-49" },
  { band: "good", min: 5, colour: "#C2B23C", label: "Good", displayRange: "50-64" },
  { band: "great", min: 6.5, colour: "#86B348", label: "Great", displayRange: "65-74" },
  { band: "firing", min: 7.5, colour: "#3E9B3E", label: "Firing", displayRange: "75-89" },
  { band: "hero", min: 9, colour: "#1E7A2C", label: "Exceptional", displayRange: "90-100" },
] as const satisfies ReadonlyArray<{
  band: ScoreBand;
  min: number;
  colour: string;
  label: string;
  displayRange: string;
}>;

export const FIRING_SCORE_MIN = 7.5;

const BAND_COLOUR = Object.fromEntries(
  SCORE_BAND_DEFINITIONS.map((definition) => [definition.band, definition.colour]),
) as Record<ScoreBand, string>;

/** Gold ring drawn around hero (9+) dots. */
export const HERO_RING_COLOUR = "#1C8A99";

export function scoreBand(score: number | null): ScoreBand | null {
  if (score == null || !Number.isFinite(score)) return null;
  for (let i = SCORE_BAND_DEFINITIONS.length - 1; i >= 0; i--) {
    const definition = SCORE_BAND_DEFINITIONS[i];
    if (score >= definition.min) return definition.band;
  }
  return "flat";
}

export function scoreColour(score: number | null): string {
  const band = scoreBand(score);
  return band ? BAND_COLOUR[band] : NA_COLOUR;
}

export function scoreBandLabel(score: number | null): string | null {
  const band = scoreBand(score);
  if (!band) return null;
  return SCORE_BAND_DEFINITIONS.find((definition) => definition.band === band)?.label ?? null;
}

/**
 * Tailwind text-colour class for a score's band, tuned to stay legible on
 * light/white cards. Use this when a band-coloured number sits on a pale
 * surface. Unlike `scoreBadgeColor().text` (always `text-white`), this is
 * only legible on its matching coloured fill. Every class clears WCAG AA
 * large-text contrast (≥3:1) on white. Returns the muted token for no score.
 */
const BAND_TEXT_CLASS: Record<ScoreBand, string> = {
  flat: "text-red-600",
  poor: "text-orange-700",
  fair: "text-orange-600",
  good: "text-amber-600",
  great: "text-lime-600",
  firing: "text-emerald-600",
  hero: "text-emerald-700",
};

export function scoreBandTextClass(score: number | null): string {
  const band = scoreBand(score);
  return band ? BAND_TEXT_CLASS[band] : "text-text-muted";
}

export function isHeroScore(score: number | null): boolean {
  return score != null && Number.isFinite(score) && score >= 9.0;
}
